import clsx from 'clsx'
import noop from 'lodash/noop'
import React, { forwardRef, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgCheckmark from '@Assets/SVG/InColor/Checkmark'
import { SizeSelector } from '@Components/SizeSelector'
import { Button } from '@CoreComponents/Buttons/Button'
import { Product, useSubscribeToBackInStockMutation } from '@Generated/graphql'
import { useFilteredVariants } from '@Globals/Product/useFilteredVariants'
import { useTranslation } from '@Globals/Translations'
import { EmailValidationV2 } from '@Shared/Components/Forms/Validations/EmailValidation'
import { TextInput, TextInputActionProps } from '@Shared/Components/Inputs/TextInput/TextInput'

import styles from './SizeOutOfStock.module.scss'

interface SizeOutOfStockProp {
  className?: HTMLDivElement['className']
  onAbort?: () => void
  product: Product
  selectedSize?: string
  showSizeSelector?: boolean
}

export const SizeOutOfStock = forwardRef<HTMLDivElement, SizeOutOfStockProp>(({
  className,
  onAbort = noop,
  product,
  selectedSize,
  showSizeSelector,
}, ref) => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const emailActionRef = useRef<TextInputActionProps | null>(null)
  const [email, setEmail] = useState<string>('')
  const [success, setSuccess] = useState(false)
  const [size, setSize] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  // Including "dead" variants doesn't really make much sense if you think about it, but the selector in this
  // module will (likely) only be shown when no variants on the product are available, which is a very narrow
  // use-case in itself. In that case, the user might hold out hope that it'll come back in stock at some point,
  // and who are we to deny their dreams? No-one, that's who.
  const { availableVariants } = useFilteredVariants({
    includeVariantsThatWillNeverRestock: true,
    product,
  })
  const [subscribeToBackInStock] = useSubscribeToBackInStockMutation()

  return (
    <div className={clsx(className, styles.container)} ref={ref}>
      <p className={styles.title}>{t(availableVariants.length > 0 ? 'cms:SIZE_IS_OUT_OF_STOCK' : 'cms:OUT_OF_STOCK')}</p>
      <p>{t(availableVariants.length > 0 ? 'cms:WANT_TO_BE_NOTIFIED_WHEN_SIZE_IS_AVAILABLE_AGAIN' : 'products:PP_OUT_OF_STOCK_WANT_TO_BE_NOTIFIED')}</p>
      <form
        onChange={() => {
          setError(null)
          setSuccess(false)
        }}
        onSubmit={async (e) => {
          e.preventDefault()
          e.stopPropagation()

          if (!emailActionRef.current?.validate()) {
            setError(t('common:COF_YOUR_EMAIL_IS_INVALID'))
            return
          }

          setIsSubmitting(true)

          let isSuccess = false
          try {
            await subscribeToBackInStock({
              variables: {
                email: email || '',
                productID: size || selectedSize || product.id || '',
              },
            })

            // Let's tell the parent component we have nothing more to do here.
            onAbort()

            setEmail('')
            emailActionRef.current?.reset()

            isSuccess = true
          } catch (err) {
            onAbort()
            setError(t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US'))
          } finally {
            ampli.productOutOfStockOptInRes({
              is_success: isSuccess,
              is_size_product: availableVariants.length !== 0,
              is_email_added: !!email,
              is_phone_number_added: false,
            })
          }
          setSuccess(isSuccess)
          setIsSubmitting(false)
        }}
      >
        {showSizeSelector && availableVariants.length > 0 && (
          <SizeSelector
            className={styles.sizeSelect}
            hideMySizeIsNotHere
            hideOutOfStockLabel
            hidePlaceholder
            name="size"
            onChange={e => e.target.value && setSize(e.target.value)}
            sizes={availableVariants}
            value={size}
          />
        )}

        <TextInput
          actionRef={emailActionRef}
          className={styles.textInput}
          id="sizeOutOfStockEmail"
          label={`${t('common:EMAIL_ADDRESS')} *`}
          onChange={e => setEmail(e.trim())}
          validations={[EmailValidationV2]}
          value={email}
        />

        <Button
          disabled={isSubmitting || success || !!error}
          innerSpacing="large"
          loading={isSubmitting}
          theme="black"
          type="submit"
        >
          {t('cms:NOTIFY_ME')}
        </Button>

        {success && (
          <div className={styles.success}>
            <SvgCheckmark />
            <p>{t('cms:PP_OUT_OF_STOCK_OVERLAY_CONTACT_YOU_SOON')}</p>
          </div>
        )}

        {error && <p className={styles.error}>{error}</p>}
      </form>
    </div>
  )
})
